import React, { useState, useMemo } from 'react';
import { t } from 'i18next';
import i18n from 'lang/i18n';
import { RawTrade } from '../function/type'; // Kiểm tra lại path import

interface TradingCalendarProps {
  rawTrades?: RawTrade[];
  onDateClick: (formattedDate: string, tradesOfDay: RawTrade[]) => void;
}

// Hàm hỗ trợ format ngày local thành "YYYY-MM-DD"
const getDateKey = (dateStr: string) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatCompactPnl = (value: number): string => {
  const sign = value < 0 ? '-' : '+';
  const abs = Math.abs(value);
  return abs >= 1000 ? `${sign}${(abs / 1000).toFixed(1)}k` : `${sign}${abs.toFixed(0)}`;
};

const TradingCalendar: React.FC<TradingCalendarProps> = ({ rawTrades = [], onDateClick }) => {
  const [viewDate, setViewDate] = useState(new Date());

  const locale = i18n.language === 'vi' ? 'vi-VN' : 'en-US';
  const monthNames = Array.from({ length: 12 }, (_, m) => {
    const name = new Date(2024, m, 1).toLocaleDateString(locale, { month: 'long' });
    return name.charAt(0).toUpperCase() + name.slice(1);
  });

  const handlePrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  const handleToday = () => {
    const now = new Date();
    setViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  // 1. Map lệnh theo Ngày ("YYYY-MM-DD" => RawTrade[])
  const tradesByDateMap = useMemo(() => {
    const map = new Map<string, RawTrade[]>();
    rawTrades.forEach(trade => {
      const key = getDateKey(trade.close_time || trade.open_time);
      if (key) {
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(trade);
      }
    });
    return map;
  }, [rawTrades]);

  // 2. Thống kê dữ liệu của cả Tháng hiện tại
  const monthlyStats = useMemo(() => {
    let totalPnL = 0;
    let tradingDaysCount = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const mStr = String(month + 1).padStart(2, '0');
      const dStr = String(day).padStart(2, '0');
      const dateKey = `${year}-${mStr}-${dStr}`;
      const dayTrades = tradesByDateMap.get(dateKey) || [];

      if (dayTrades.length > 0) {
        tradingDaysCount++;
        totalPnL += dayTrades.reduce((acc, t) => {
          const gross = t.profit_gross ?? ((t.net_profit || 0) - (t.commission || 0) - (t.swap || 0));
          return acc + gross;
        }, 0);
      }
    }

    return { totalPnL, tradingDaysCount };
  }, [tradesByDateMap, year, month, daysInMonth]);

  return (
    <div className="bg-white rounded-xl shadow-premium border border-slate-100 p-3 sm:p-6 flex flex-col">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-2 sm:gap-4 mb-3 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={handleToday}
            className="whitespace-nowrap px-3 sm:px-4 py-2 border border-slate-200 !text-slate-700 !text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors shadow-sm bg-white"
          >
            {t('metrixLive.calendar.today')}
          </button>
          <div className="flex items-center gap-3">
            <button onClick={handlePrevMonth} className="p-2 border border-slate-100 rounded bg-slate-50 !text-slate-500 hover:bg-slate-100 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <h2 className="!text-base sm:!text-lg font-bold !text-slate-900 !m-0 whitespace-nowrap sm:min-w-[170px] !text-center">
              {monthNames[month]} {year}
            </h2>
            <button onClick={handleNextMonth} className="p-2 border border-slate-100 rounded bg-slate-50 !text-slate-500 hover:bg-slate-100 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
        {/* Phones: monthly P/L and trading days share one row */}
        <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-4 flex-wrap">
          <div className="!text-xs sm:!text-sm whitespace-nowrap">
            <span className="!text-slate-500 font-medium mr-1 sm:mr-2">{t('metrixLive.calendar.monthlyPnl')}:</span>
            <span className={`font-bold px-2 py-1 rounded font-mono-num ${monthlyStats.totalPnL >= 0 ? '!text-teal-500 bg-teal-50' : '!text-rose-500 bg-rose-50'}`}>
              {monthlyStats.totalPnL >= 0 ? `$${monthlyStats.totalPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `-$${Math.abs(monthlyStats.totalPnL).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </span>
          </div>
          <div className="whitespace-nowrap bg-slate-100 border border-slate-200 !text-slate-600 !text-xs sm:!text-sm font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg shadow-sm">
            {t('metrixLive.calendar.tradingDays')}: <span className="font-bold !text-slate-900 ml-1">{monthlyStats.tradingDaysCount}</span>
          </div>
        </div>
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-200 gap-px grid grid-cols-7">
        {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((key) => t(`metrixLive.calendar.wd.${key}`)).map(d => (
          <div key={d} className="bg-white p-1.5 sm:p-3 !text-xs sm:!text-sm font-semibold !text-slate-500 text-center sm:text-left">{d}</div>
        ))}
        {/* Only as many weeks as the month needs (4–6), instead of always 6 rows */}
        {Array.from({ length: Math.ceil((startOffset + daysInMonth) / 7) * 7 }, (_, i) => {
          const dayIndex = i - startOffset;
          const isCurrentMonth = dayIndex >= 0 && dayIndex < daysInMonth;

          let displayDay: number;
          if (dayIndex < 0) {
            displayDay = daysInPrevMonth + dayIndex + 1;
          } else if (dayIndex >= daysInMonth) {
            displayDay = dayIndex - daysInMonth + 1;
          } else {
            displayDay = dayIndex + 1;
          }

          // Lấy dữ liệu lệnh cho từng ô ô calendar
          const mStr = String(month + 1).padStart(2, '0');
          const dStr = String(displayDay).padStart(2, '0');
          const dateKey = `${year}-${mStr}-${dStr}`;

          const dayTrades = isCurrentMonth ? (tradesByDateMap.get(dateKey) || []) : [];
          const hasTrades = dayTrades.length > 0;

          const dayPnL = dayTrades.reduce((acc, t) => {
            const gross = t.profit_gross + ((t.commission || 0) + (t.swap || 0));
            return acc + gross;
          }, 0);
          const isLoss = dayPnL < 0;

          const isToday = isCurrentMonth &&
            new Date().getDate() === displayDay &&
            new Date().getMonth() === month &&
            new Date().getFullYear() === year;

          const handleDayClick = () => {
            if (!isCurrentMonth) return;
            // getDay()+1 gave "Thứ 1" for Sunday; let the locale format the weekday instead
            const weekday = new Date(year, month, displayDay).toLocaleDateString(locale, { weekday: 'long' });
            const formattedModalDate = `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${displayDay}/${month + 1}/${year}`;
            onDateClick(formattedModalDate, dayTrades);
          };

          return (
            <div
              key={i}
              className={`bg-white p-1 sm:p-2 min-h-[46px] sm:min-h-[100px] xl:min-h-[120px] flex flex-col transition-colors ${isCurrentMonth ? 'cursor-pointer hover:bg-slate-50' : 'bg-slate-50/30'}`}
              onClick={handleDayClick}
            >
              {isToday ? (
                <div className="w-6 h-6 sm:w-7 sm:h-7 bg-blue-600 rounded-full flex items-center justify-center !text-white !text-xs sm:!text-sm font-bold mb-0.5 sm:mb-1 shadow-sm">
                  {displayDay}
                </div>
              ) : (
                <span className={`!text-xs sm:!text-sm font-medium ${!isCurrentMonth ? '!text-slate-300' : '!text-slate-700'}`}>
                  {displayDay}
                </span>
              )}

              {hasTrades && (
                <div className={`mt-auto ${isLoss ? 'bg-rose-50 border-rose-100' : 'bg-[#e6fcf5] border-[#c3fae8]'} rounded p-0.5 sm:p-2 border`}>
                  <div className={`font-bold ${isLoss ? '!text-rose-500' : '!text-teal-600'} !text-[10px] sm:!text-sm font-mono-num leading-tight text-center sm:text-left`}>
                    {/* Phones: compact amount (e.g. +1.0k) so it fits the narrow cell; full amount from sm up */}
                    <span className="sm:hidden">{formatCompactPnl(dayPnL)}</span>
                    <span className="max-sm:hidden">{isLoss ? `-$${Math.abs(dayPnL).toFixed(2)}` : `$${dayPnL.toFixed(2)}`}</span>
                  </div>
                  <div className="max-sm:hidden !text-[11px] !text-slate-500 mt-1">{t('metrixLive.calendar.trades', { count: dayTrades.length })}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 !text-center">
        <p className="!text-xs font-semibold !text-slate-400">{t('metrixLive.calendar.timezoneNote')}</p>
      </div>
    </div>
  );
};

export default TradingCalendar;